import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * <CitedSources />
 *
 * Renders a visible, ordered list of authoritative external sources at the
 * bottom of a content page. Designed for AEO (Answer Engine Optimisation):
 * AI engines weight external citations to primary sources (ACSC, OAIC,
 * Microsoft Learn, digital.gov.au) as a trust signal when deciding whether
 * to ground answers in our content.
 *
 * - Ordered list (<ol>) so engines can cite "as per source 2 of …".
 * - All links open in a new tab with rel="noopener noreferrer".
 * - NO rel="nofollow" — we want engines to follow and corroborate.
 * - No JSON-LD; this component is for visible citations only.
 *
 * @param {Object} props
 * @param {string} [props.title="Authoritative sources"] - Heading text.
 * @param {Array<{title:string, publisher:string, url:string, accessed?:string}>} props.sources
 *        Array of source objects. `accessed` is optional ISO date (YYYY-MM-DD).
 */
const CitedSources = ({ title = 'Authoritative sources', sources = [] }) => {
  if (!Array.isArray(sources) || sources.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="cited-sources-heading"
      className="mt-12 rounded-2xl border border-[#E8E0F0] bg-[#F4F0FA] p-6 sm:p-8"
    >
      <h2
        id="cited-sources-heading"
        className="text-xl sm:text-2xl font-bold text-[#341671] mb-4"
      >
        {title}
      </h2>
      <p className="text-sm text-[#422680]/80 mb-5">
        This article draws on primary, authoritative sources. Each link opens in a new tab.
      </p>
      <ol className="list-decimal list-outside ml-5 space-y-3 marker:text-[#660F56] marker:font-semibold">
        {sources.map((source) => (
          <li key={source.url} className="text-[#341671] leading-relaxed">
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-baseline gap-1 font-semibold text-[#422680] underline decoration-[#422680]/30 underline-offset-2 hover:text-[#660F56] hover:decoration-[#660F56] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F54952] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F4F0FA] rounded"
            >
              <span>{source.title}</span>
              <ExternalLink
                className="w-3.5 h-3.5 flex-shrink-0 self-center"
                aria-hidden="true"
              />
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
            <span className="text-[#341671]/90"> — {source.publisher}</span>
            {source.accessed && (
              <span className="block text-xs text-[#422680]/60 mt-0.5">
                Accessed {source.accessed}
              </span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default CitedSources;
